import { formatNumber } from "./numberUtils";

const getItemLineEditorId = (item) =>
    item.lineEditorId || (item.quoteModuleGuid ? `${item.quoteModuleGuid}_${item.guid}` : item.guid);

const flattenGroupsAndItems = (groupName, quoteCustomisation, filteredQuoteItems) => {
    const updatedItems = [];
    const groups = groupName === "upfront"
        ? quoteCustomisation.groups.upfront
        : quoteCustomisation.groups.recurring;

    groups.forEach((group) => {
        const groupItem = {
            ...group,
            id: group.guid,
            name: group.name,
            rowType: "group",
            showChildren: group.showChildren,
            depth: 0,
            expanded: group.expanded,
            visible: group.visible,
        };

        updatedItems.push(groupItem);

        group.items.forEach((groupItemRef) => {
            let quoteItem = filteredQuoteItems.find((x) =>
                groupItemRef.lineEditorId
                    ? groupItemRef.lineEditorId === getItemLineEditorId(x)
                    : x.guid === groupItemRef.guid &&
                      (group.guid === x.quoteModuleGuid || x.quoteModuleGuid === null)
            );

            if (!quoteItem && !groupItemRef.lineEditorId) {
                quoteItem = filteredQuoteItems.find(
                    (x) =>
                        x.guid === groupItemRef.guid &&
                        updatedItems.some((ui) => ui.id === getItemLineEditorId(x)) === false
                );
            }

            if (quoteItem) {
                const lineEditorId = getItemLineEditorId(quoteItem);
                const childItem = {
                    ...quoteItem,
                    id: lineEditorId,
                    lineEditorId,
                    groupGuid: group.guid,
                    name: quoteItem.internalName,
                    rowType: "child",
                    type: group.type,
                    depth: 1,
                    visible: groupItemRef.visible,
                    groupIsVisible: groupItem.visible,
                };

                if (groupName === "upfront" && childItem.upfrontNameOnQuote) {
                    childItem.name = childItem.upfrontNameOnQuote;
                }

                if (groupName === "recurring" && childItem.recurringNameOnQuote) {
                    childItem.name = childItem.recurringNameOnQuote;
                }

                updatedItems.push(childItem);
            }
        });
    });

    return updatedItems;
};

const formatFieldsForDocument = (items) => {
    items.forEach((exportItem) => {
        exportItem.upfrontPrice = formatNumber(exportItem.upfrontPrice);
        exportItem.totalUpfrontPrice = formatNumber(exportItem.totalUpfrontPrice);
        exportItem.recurringPrice = formatNumber(exportItem.recurringPrice);
        exportItem.totalRecurringPrice = formatNumber(exportItem.totalRecurringPrice);
        exportItem.annualPrice = formatNumber(exportItem.annualPrice);
        exportItem.totalAnnualPrice = formatNumber(exportItem.totalAnnualPrice);
        exportItem.totalContractPrice = formatNumber(exportItem.totalContractPrice);

        if (exportItem.rowType === "child") {
            exportItem.nameOnQuote = exportItem.name;
        }
    });
};

const removeUnnecessaryProperties = (items) => {
    items.forEach((exportItem) => {
        delete exportItem.items;
        delete exportItem.expanded;
        delete exportItem.id;
        delete exportItem.isRecurring;
        delete exportItem.isUpfront;
        delete exportItem.quoteRevision;
        delete exportItem.quoteRevisionId;

        if (exportItem.rowType === "child") {
            delete exportItem.name;
        } else if (exportItem.rowType === "group") {
            if (exportItem.showChildren) {
                delete exportItem.quantity;
                delete exportItem.upfrontPrice;
                delete exportItem.recurringPrice;
                delete exportItem.annualPrice;
                delete exportItem.totalUpfrontPrice;
                delete exportItem.totalRecurringPrice;
                delete exportItem.totalAnnualPrice;
            }
        }
    });
};

const removeHiddenItems = (items, groups) => {
    return items.filter(
        (item) =>
            item.visible === true &&
            (item.rowType === "group" ||
                (item.rowType === "child" &&
                    groups.find(
                        (group) =>
                            group.guid === item.groupGuid &&
                            group.showChildren === true &&
                            group.visible === true
                    )))
    );
};

const collapseGroupsOnToFirstChild = (items) => {
    const collapsedItems = [];

    for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.rowType === "group" && item.showChildren) {
            const nextItem = items[i + 1];
            if (nextItem && nextItem.rowType === "child") {
                collapsedItems.push({
                    ...nextItem,
                    name: item.name,
                });
                i++;
            } else {
                collapsedItems.push(item);
            }
        } else {
            collapsedItems.push(item);
        }
    }

    return collapsedItems;
};

/**
 * Builds the export-ready grouped quote data from a quote customisation structure
 * and its flat quote items.
 *
 * Mirrors the flattening / filtering / formatting logic previously duplicated in
 * useExportRequestData, so admin tester previews and real quote pages produce the
 * same document-export item shape.
 *
 * @param {object} quoteCustomisation - Object with groups.upfront / groups.recurring arrays.
 * @param {Array} quoteItems - Flat array of quote line items.
 * @returns {{ upfrontItems: Array, recurringItems: Array, exportData: {upfront: Array, recurring: Array} }}
 */
export const buildExportDataFromQuoteCustomisation = (quoteCustomisation, quoteItems) => {
    const qc = quoteCustomisation || {};
    const normalisedQuoteCustomisation = {
        ...qc,
        groups: {
            upfront: Array.isArray(qc?.groups?.upfront) ? qc.groups.upfront : [],
            recurring: Array.isArray(qc?.groups?.recurring) ? qc.groups.recurring : [],
        },
    };

    const upfrontItems = flattenGroupsAndItems("upfront", normalisedQuoteCustomisation, quoteItems);
    const recurringItems = flattenGroupsAndItems("recurring", normalisedQuoteCustomisation, quoteItems);

    const exportData = {
        upfront: structuredClone(upfrontItems),
        recurring: structuredClone(recurringItems),
    };

    exportData.upfront = removeHiddenItems(exportData.upfront, normalisedQuoteCustomisation.groups.upfront);
    exportData.recurring = removeHiddenItems(exportData.recurring, normalisedQuoteCustomisation.groups.recurring);

    formatFieldsForDocument(exportData.upfront);
    formatFieldsForDocument(exportData.recurring);

    removeUnnecessaryProperties(exportData.upfront);
    removeUnnecessaryProperties(exportData.recurring);

    exportData.upfront = collapseGroupsOnToFirstChild(exportData.upfront).filter(
        (x) => x.quantity !== undefined
    );
    exportData.recurring = collapseGroupsOnToFirstChild(exportData.recurring).filter(
        (x) => x.quantity !== undefined
    );

    return { upfrontItems, recurringItems, exportData };
};
